// Events Data - Catalysts, Calendar, Timeline, Risks

export interface Catalyst {
  id: string;
  event: string;
  date: string;
  daysUntil: number;
  impact: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  impactScore: number;
  category: string;
  description: string;
  marketEffect: string;
  probability: number;
  priceIn: number;
}

export interface LiveEvent {
  time: string;
  event: string;
  category: string;
  impact: 'CRITICAL' | 'HIGH' | 'MED' | 'LOW';
  description?: string;
}

export interface CalendarEvent {
  date: string;
  event: string;
  previous: string;
  forecast: string;
  category: string;
  importance: number; // 1-5
}

export interface Reform {
  reform: string;
  category: string;
  progress: number;
  target: number;
  targetDate: string;
  status: 'ON TRACK' | 'IN PROGRESS' | 'DELAYED';
  milestones: { name: string; done: boolean }[];
}

export interface TimelineEvent {
  year: number;
  month: number;
  event: string;
  category: string;
  impact: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  description: string;
  future?: boolean;
}

export interface Risk {
  risk: string;
  category: string;
  probability: number;
  impact: 'HIGH' | 'MEDIUM' | 'LOW';
  trend: 'ELEVATED' | 'STABLE' | 'DECLINING';
  description: string;
  mitigation: string;
}

export interface Deal {
  deal: string;
  value: number; // millions
  stage: string;
  sector: string;
  expected: string;
}

export const eventsData = {
  catalysts: [
    {
      id: 'wto-accession',
      event: 'WTO Accession',
      date: '2026-03-15',
      daysUntil: 93,
      impact: 'CRITICAL' as const,
      impactScore: 98,
      category: 'TRADE',
      description: 'Full WTO membership at 14th Ministerial Conference',
      marketEffect: 'Tariff reduction, FDI surge, market access',
      probability: 92,
      priceIn: 35,
    },
    {
      id: 'bank-privatization',
      event: 'Major Bank Privatizations',
      date: '2025-06-30',
      daysUntil: 200,
      impact: 'HIGH' as const,
      impactScore: 85,
      category: 'REFORM',
      description: 'IPO of state banks (Asaka, Ipoteka)',
      marketEffect: 'Capital markets development, foreign investment',
      probability: 78,
      priceIn: 20,
    },
    {
      id: 'eurobond-2025',
      event: 'Sovereign Eurobond Issue',
      date: '2025-Q2',
      daysUntil: 150,
      impact: 'HIGH' as const,
      impactScore: 82,
      category: 'POLICY',
      description: '$1B+ USD-denominated bond issuance',
      marketEffect: 'Yield benchmark, investor confidence',
      probability: 85,
      priceIn: 45,
    },
    {
      id: 'rating-upgrade',
      event: 'Potential BB+ Upgrade',
      date: '2025-H2',
      daysUntil: 270,
      impact: 'HIGH' as const,
      impactScore: 88,
      category: 'RATING',
      description: 'S&P/Fitch upgrade to BB+ (investment grade path)',
      marketEffect: 'Lower borrowing costs, index inclusion',
      probability: 65,
      priceIn: 15,
    },
    {
      id: 'it-park-expansion',
      event: 'IT Park Phase 2 Launch',
      date: '2025-04-01',
      daysUntil: 110,
      impact: 'MEDIUM' as const,
      impactScore: 68,
      category: 'REFORM',
      description: 'Expansion of tech hub with new incentives',
      marketEffect: 'Tech sector growth, startup ecosystem',
      probability: 95,
      priceIn: 60,
    },
    {
      id: 'energy-reform',
      event: 'Energy Tariff Liberalization',
      date: '2025-07-01',
      daysUntil: 201,
      impact: 'MEDIUM' as const,
      impactScore: 72,
      category: 'REFORM',
      description: 'Full cost recovery on electricity/gas',
      marketEffect: 'Fiscal improvement, efficiency gains',
      probability: 88,
      priceIn: 50,
    },
  ] as Catalyst[],

  liveFeed: [
    { time: '2024-12-12 14:32', event: 'CBU holds policy rate at 13.5%', category: 'POLICY', impact: 'MED' as const },
    { time: '2024-12-11 09:15', event: 'S&P affirms BB- rating, stable outlook', category: 'RATING', impact: 'HIGH' as const },
    { time: '2024-12-10 16:45', event: 'Masdar signs $1.5B solar MOU', category: 'FDI', impact: 'HIGH' as const },
    { time: '2024-12-09 11:20', event: 'WTO bilateral with Japan completed', category: 'TRADE', impact: 'MED' as const },
    { time: '2024-12-08 08:00', event: 'November inflation: 9.0% YoY', category: 'ECONOMIC', impact: 'MED' as const },
    { time: '2024-12-06 14:10', event: 'Uzum raises $100M Series C', category: 'FDI', impact: 'MED' as const },
    { time: '2024-12-05 10:30', event: 'Parliament approves 2025 budget', category: 'POLICY', impact: 'HIGH' as const },
    { time: '2024-12-04 09:00', event: 'FX reserves reach $49.7B', category: 'ECONOMIC', impact: 'MED' as const },
    { time: '2024-12-02 15:45', event: 'KIA Motors breaks ground on plant', category: 'FDI', impact: 'HIGH' as const },
    { time: '2024-11-29 12:00', event: 'Digital bank licenses: 3 approved', category: 'REFORM', impact: 'MED' as const },
    { time: '2024-11-21 09:30', event: 'Moody\'s upgrades to Ba3', category: 'RATING', impact: 'CRITICAL' as const },
    { time: '2024-11-15 14:00', event: 'Q3 GDP growth: 6.5% YoY', category: 'ECONOMIC', impact: 'HIGH' as const },
  ] as LiveEvent[],

  economicCalendar: [
    { date: '2024-12-15', event: 'Trade Balance (Nov)', previous: '-$0.8B', forecast: '-$0.7B', category: 'DATA', importance: 2 },
    { date: '2024-12-20', event: 'CBU Policy Meeting', previous: '13.5%', forecast: '13.5%', category: 'POLICY', importance: 3 },
    { date: '2024-12-28', event: 'FX Reserves Update', previous: '$49.7B', forecast: '$50.0B', category: 'DATA', importance: 2 },
    { date: '2025-01-10', event: 'CPI Inflation (Dec)', previous: '9.0%', forecast: '8.8%', category: 'DATA', importance: 3 },
    { date: '2025-01-15', event: 'Industrial Production', previous: '+7.2%', forecast: '+7.0%', category: 'DATA', importance: 2 },
    { date: '2025-01-20', event: 'Q4 2024 GDP Preliminary', previous: '6.5%', forecast: '6.3%', category: 'DATA', importance: 4 },
    { date: '2025-01-25', event: 'FDI Report (Full Year)', previous: '$7.2B', forecast: '$11.9B', category: 'DATA', importance: 4 },
    { date: '2025-02-01', event: 'CBU Policy Meeting', previous: '13.5%', forecast: '13.0%', category: 'POLICY', importance: 3 },
    { date: '2025-02-10', event: 'Budget Execution Report', previous: '-3.0%', forecast: '-2.8%', category: 'DATA', importance: 2 },
    { date: '2025-02-15', event: 'Remittances Data (Jan)', previous: '$1.2B', forecast: '$1.1B', category: 'DATA', importance: 2 },
    { date: '2025-03-01', event: 'WTO Progress Report', previous: '91%', forecast: '97%', category: 'TRADE', importance: 3 },
    { date: '2025-03-15', event: 'WTO Ministerial Conference', previous: 'N/A', forecast: 'ACCESSION', category: 'TRADE', importance: 5 },
  ] as CalendarEvent[],

  reformTracker: [
    {
      reform: 'WTO Accession',
      category: 'TRADE',
      progress: 91,
      target: 100,
      targetDate: '2026-03',
      status: 'ON TRACK' as const,
      milestones: [
        { name: 'US Bilateral', done: true },
        { name: 'EU Bilateral', done: true },
        { name: 'China Bilateral', done: true },
        { name: 'Final 3 Countries', done: false },
        { name: 'Ministerial Vote', done: false },
      ],
    },
    {
      reform: 'Bank Privatization',
      category: 'REFORM',
      progress: 45,
      target: 100,
      targetDate: '2027-12',
      status: 'IN PROGRESS' as const,
      milestones: [
        { name: 'Strategy Approved', done: true },
        { name: 'Advisors Hired', done: true },
        { name: 'Due Diligence', done: false },
        { name: 'IPO Preparation', done: false },
        { name: 'Market Listing', done: false },
      ],
    },
    {
      reform: 'Energy Subsidy Reform',
      category: 'REFORM',
      progress: 67,
      target: 100,
      targetDate: '2027-12',
      status: 'ON TRACK' as const,
      milestones: [
        { name: 'Phase 1 (30%)', done: true },
        { name: 'Phase 2 (50%)', done: true },
        { name: 'Phase 3 (70%)', done: true },
        { name: 'Full Cost Recovery', done: false },
        { name: 'Targeted Subsidies', done: false },
      ],
    },
    {
      reform: 'Digital Uzbekistan 2030',
      category: 'DIGITAL',
      progress: 52,
      target: 100,
      targetDate: '2030-12',
      status: 'ON TRACK' as const,
      milestones: [
        { name: 'E-Government', done: true },
        { name: 'Digital ID', done: true },
        { name: '5G Infrastructure', done: false },
        { name: 'Data Centers', done: false },
        { name: 'AI Strategy', done: false },
      ],
    },
    {
      reform: 'Capital Markets Development',
      category: 'REFORM',
      progress: 38,
      target: 100,
      targetDate: '2028-12',
      status: 'IN PROGRESS' as const,
      milestones: [
        { name: 'Exchange Reform', done: true },
        { name: 'Securities Law', done: true },
        { name: 'Central Depository', done: false },
        { name: 'Market Makers', done: false },
        { name: 'Index Inclusion', done: false },
      ],
    },
    {
      reform: 'Renewable Energy 54%',
      category: 'ENERGY',
      progress: 37,
      target: 54,
      targetDate: '2030-12',
      status: 'ON TRACK' as const,
      milestones: [
        { name: '10% Renewables', done: true },
        { name: '20% Renewables', done: true },
        { name: 'Solar Auctions', done: false },
        { name: 'Wind Projects', done: false },
        { name: '54% Target', done: false },
      ],
    },
  ] as Reform[],

  timeline: [
    { year: 2016, month: 9, event: 'Mirziyoyev becomes President', category: 'POLITICAL', impact: 'CRITICAL' as const, description: 'New reform era begins' },
    { year: 2017, month: 9, event: 'Currency Liberalization', category: 'REFORM', impact: 'CRITICAL' as const, description: 'UZS floats freely, 50% devaluation' },
    { year: 2017, month: 12, event: 'Visa-Free for 65 Countries', category: 'REFORM', impact: 'HIGH' as const, description: 'Tourism and business access opens' },
    { year: 2018, month: 6, event: 'FX Unification', category: 'REFORM', impact: 'HIGH' as const, description: 'Single exchange rate established' },
    { year: 2019, month: 2, event: 'First Eurobond ($1B)', category: 'POLICY', impact: 'HIGH' as const, description: 'Debut on international markets' },
    { year: 2020, month: 7, event: 'WTO Negotiations Restart', category: 'TRADE', impact: 'HIGH' as const, description: 'Accession process accelerates' },
    { year: 2021, month: 3, event: 'IT Park Tax Incentives', category: 'REFORM', impact: 'MEDIUM' as const, description: '0% income tax for tech' },
    { year: 2022, month: 6, event: 'Crypto Regulation', category: 'REFORM', impact: 'MEDIUM' as const, description: 'NAPP established, licenses issued' },
    { year: 2023, month: 3, event: 'First Unicorn: Uzum', category: 'MILESTONE', impact: 'HIGH' as const, description: '$1.16B valuation achieved' },
    { year: 2023, month: 6, event: "Moody's Upgrade to Ba3", category: 'RATING', impact: 'HIGH' as const, description: 'First rating upgrade' },
    { year: 2024, month: 3, event: 'Second Unicorn: TBC UZ', category: 'MILESTONE', impact: 'MEDIUM' as const, description: 'Banking sector unicorn' },
    { year: 2024, month: 12, event: 'FDI Record: $11.9B', category: 'FDI', impact: 'HIGH' as const, description: '+53.6% YoY growth' },
    { year: 2025, month: 6, event: 'Potential BB Upgrade', category: 'RATING', impact: 'HIGH' as const, description: 'Second agency upgrade', future: true },
    { year: 2026, month: 3, event: 'WTO Full Membership', category: 'TRADE', impact: 'CRITICAL' as const, description: 'Accession complete', future: true },
    { year: 2026, month: 6, event: 'Bank IPOs Begin', category: 'REFORM', impact: 'HIGH' as const, description: 'Asaka Bank listing', future: true },
    { year: 2027, month: 12, event: 'Investment Grade Path', category: 'RATING', impact: 'CRITICAL' as const, description: 'BBB- target', future: true },
    { year: 2030, month: 12, event: 'Digital UZ Complete', category: 'REFORM', impact: 'HIGH' as const, description: 'Full digitalization', future: true },
  ] as TimelineEvent[],

  risks: [
    {
      risk: 'Regional Geopolitical Tensions',
      category: 'GEOPOLITICAL',
      probability: 25,
      impact: 'HIGH' as const,
      trend: 'STABLE' as const,
      description: 'Russia-Ukraine spillover, sanctions risk',
      mitigation: 'Diversified trade partners, neutral stance',
    },
    {
      risk: 'Commodity Price Volatility',
      category: 'ECONOMIC',
      probability: 45,
      impact: 'MEDIUM' as const,
      trend: 'ELEVATED' as const,
      description: 'Gold, gas, cotton price swings',
      mitigation: 'Reserve buffer, hedging programs',
    },
    {
      risk: 'WTO Accession Delay',
      category: 'TRADE',
      probability: 15,
      impact: 'HIGH' as const,
      trend: 'DECLINING' as const,
      description: 'Bilateral holdouts, procedural delays',
      mitigation: '31/34 deals done, strong momentum',
    },
    {
      risk: 'Inflation Persistence',
      category: 'ECONOMIC',
      probability: 35,
      impact: 'MEDIUM' as const,
      trend: 'DECLINING' as const,
      description: 'Above-target inflation continues',
      mitigation: 'CBU tightening, fiscal discipline',
    },
    {
      risk: 'Reform Fatigue',
      category: 'POLITICAL',
      probability: 20,
      impact: 'MEDIUM' as const,
      trend: 'STABLE' as const,
      description: 'Slowing pace of structural reforms',
      mitigation: 'IMF program, WTO commitment',
    },
    {
      risk: 'FX Pressure',
      category: 'ECONOMIC',
      probability: 30,
      impact: 'MEDIUM' as const,
      trend: 'STABLE' as const,
      description: 'UZS depreciation, capital outflows',
      mitigation: '$49.7B reserves (10 months cover)',
    },
  ] as Risk[],

  dealPipeline: [
    { deal: 'Masdar Solar Complex', value: 1500, stage: 'MOU SIGNED', sector: 'ENERGY', expected: '2025-Q1' },
    { deal: 'Lukoil Gas Expansion', value: 2200, stage: 'NEGOTIATION', sector: 'ENERGY', expected: '2025-Q2' },
    { deal: 'Asaka Bank IPO', value: 500, stage: 'PREPARATION', sector: 'FINANCE', expected: '2026-Q2' },
    { deal: 'KIA Auto Plant', value: 800, stage: 'CONSTRUCTION', sector: 'MANUFACTURING', expected: '2025-Q4' },
    { deal: 'DP World Logistics Hub', value: 500, stage: 'MOU SIGNED', sector: 'INFRASTRUCTURE', expected: '2025-Q2' },
    { deal: 'TBC Digital Bank', value: 300, stage: 'LICENSED', sector: 'FINTECH', expected: '2025-Q1' },
    { deal: 'Indorama Textile II', value: 600, stage: 'PLANNING', sector: 'MANUFACTURING', expected: '2026-Q1' },
    { deal: 'Air Products Chemicals', value: 220, stage: 'ANNOUNCED', sector: 'CHEMICALS', expected: '2025-Q3' },
  ] as Deal[],

  summary: {
    upcomingCatalysts: 6,
    avgImpactScore: 82,
    reformsOnTrack: 4,
    reformsInProgress: 2,
    reformsDelayed: 0,
    riskLevel: 'MODERATE' as 'LOW' | 'MODERATE' | 'HIGH',
    dealPipelineValue: 6.62,
    nextMajorEvent: 'WTO Accession',
    daysToWTO: 93,
  },
};

// Category colors
export const categoryColors: Record<string, string> = {
  RATING: '#fbbf24',    // amber
  REFORM: '#00d4d4',    // cyan
  TRADE: '#a855f7',     // purple
  FDI: '#00d26a',       // green
  POLICY: '#00a3ff',    // blue
  ECONOMIC: '#6b7280',  // gray
  DATA: '#6b7280',      // gray
  POLITICAL: '#f59e0b', // orange
  GEOPOLITICAL: '#ef4444', // red
  DIGITAL: '#00d4d4',   // cyan
  ENERGY: '#22c55e',    // green
  MILESTONE: '#00d26a', // green
};
